// The support editor's controller: a FROZEN COPY of `ui--text-editor` as it
// stood at conek_ui v0.2.8, the version conek-rails renders today. The main
// one has moved a long way since and the two are deliberately independent —
// a fix here should be a fix to THIS editor, never a port from that one.
// Rich text editor controller.
//
// Two engines behind one public API.
//
// `lexxy` (the default) wraps the <lexxy-editor> custom element the lexxy gem
// defines. The gem owns the editing surface, the toolbar and the formatting
// commands; this controller owns everything around it:
//
//   • the TOOLBAR ALLOW-LIST. Lexxy builds its full toolbar in JavaScript, so a
//     server-rendered subset is impossible — the component publishes the button
//     names it wants as a value and the controller hides the rest once the
//     element upgrades. `hidden` attributes only, no injected <style>, so the
//     technique survives a strict CSP.
//   • the EVENT SURFACE. Consumers bind to ui--text-editor-support:change / :focus /
//     :blur and never to the gem's own lexxy:* events, so the Lexxy dependency
//     stops at this file.
//   • the PUBLIC API below, which is how the Work kit (comments, issue
//     composer) reads and writes the editor. Nothing outside this controller
//     should touch the editor element or a hidden input directly — that
//     coupling is exactly what broke when the contenteditable went away.
//   • the TURBO FRAME re-initialisation workaround, scoped to this element (the
//     host carries a document-wide copy that can retire once this ships).
//
// `basic` is the pre-Lexxy contenteditable + execCommand editor, kept verbatim
// so existing surfaces keep working during rollout. It is DEPRECATED and goes
// away with the component's `engine:` prop in the next minor.

import { Controller } from "@hotwired/stimulus"

// Toolbar controls that are dropdown wrappers rather than plain buttons. Their
// visibility is decided by the name on the trigger INSIDE them, and the whole
// wrapper is hidden — a bare wrapper is still a flex item and would leave a gap.
const DROPDOWN_TAGS = "lexxy-toolbar-dropdown, lexxy-highlight-dropdown, lexxy-link-dropdown"

export default class extends Controller {
  static targets = ["editor", "hidden", "toolbar", "toolbarButton"]
  static values = { disabled: Boolean, engine: String, toolbar: Array }

  connect() {
    if (this.isLexxy) {
      this.connectLexxy()
    } else {
      this.syncHiddenInput()
      this.setupPlaceholder()
      this.setupKeyboardShortcuts()
    }
  }

  disconnect() {
    this.teardownLexxy?.()
  }

  get isLexxy() {
    // The value is absent on the read-only branch of older markup; `lexxy` is
    // the component's default, so treat anything that is not `basic` as Lexxy.
    return this.engineValue !== "basic"
  }

  // The <lexxy-editor> element, or null on the read-only branch (which renders
  // sanitized HTML and a hidden input, with no editor at all).
  get editorElement() {
    if (!this.hasEditorTarget) return null
    return this.editorTarget.tagName.toLowerCase() === "lexxy-editor" ? this.editorTarget : null
  }

  // ---------------------------------------------------------------- public API

  // The document as HTML — what the field submits.
  getHTML() {
    if (this.isLexxy) {
      const editor = this.editorElement
      if (editor) return editor.value ?? ""
      return this.hasHiddenTarget ? this.hiddenTarget.value : ""
    }
    return this.hasEditorTarget ? this.editorTarget.innerHTML : ""
  }

  // The document as plain text — for drafts, length checks and announcements.
  getText() {
    if (this.isLexxy) {
      const editor = this.editorElement
      if (editor) return editor.toString() ?? ""
      return this.stripTags(this.hasHiddenTarget ? this.hiddenTarget.value : "")
    }
    if (!this.hasEditorTarget) return ""
    return this.editorTarget.innerText || this.editorTarget.textContent || ""
  }

  setValue(html) {
    const value = html ?? ""
    if (this.isLexxy) {
      const editor = this.editorElement
      if (editor) editor.value = value
      if (this.hasHiddenTarget) this.hiddenTarget.value = value
      return
    }
    if (this.hasEditorTarget) this.editorTarget.innerHTML = value
    this.syncHiddenInput()
  }

  clear() {
    this.setValue("")
  }

  focus() {
    if (this.isLexxy) {
      this.editorElement?.focus()
      return
    }
    if (this.hasEditorTarget) this.editorTarget.focus()
  }

  isEmpty() {
    if (this.isLexxy) {
      const editor = this.editorElement
      if (editor) return editor.isEmpty
      return this.getText().trim() === ""
    }
    return this.getText().trim() === "" && !this.editorTarget?.querySelector("img")
  }

  // ------------------------------------------------------------- lexxy engine

  connectLexxy() {
    const editor = this.editorElement

    this.onLexxyInitialize = () => this.applyToolbarAllowList()
    this.onLexxyChange = () =>
      this.emit("change", { html: this.getHTML(), text: this.getText() })
    this.onLexxyFocus = () => this.emit("focus")
    this.onLexxyBlur = () => this.emit("blur")
    this.onFrameLoad = () => this.reinitializeStalledEditor()

    if (editor) {
      editor.addEventListener("lexxy:initialize", this.onLexxyInitialize)
      editor.addEventListener("lexxy:change", this.onLexxyChange)
      editor.addEventListener("lexxy:focus", this.onLexxyFocus)
      editor.addEventListener("lexxy:blur", this.onLexxyBlur)
    }
    document.addEventListener("turbo:frame-load", this.onFrameLoad)

    this.teardownLexxy = () => {
      if (editor) {
        editor.removeEventListener("lexxy:initialize", this.onLexxyInitialize)
        editor.removeEventListener("lexxy:change", this.onLexxyChange)
        editor.removeEventListener("lexxy:focus", this.onLexxyFocus)
        editor.removeEventListener("lexxy:blur", this.onLexxyBlur)
      }
      document.removeEventListener("turbo:frame-load", this.onFrameLoad)
      this.teardownLexxy = null
    }

    // The element can upgrade BEFORE Stimulus connects this controller: the
    // gem's module is loaded in <head> while the controller bundle is deferred.
    // lexxy:initialize has already fired by then and never fires again, so the
    // allow-list has to be applied on the spot.
    this.applyToolbarAllowList()
  }

  // Hides every toolbar control the component did not ask for. Only TOP-LEVEL
  // controls are considered: the buttons inside a dropdown's panel (Normal,
  // Large Heading, …) are menu items, not toolbar controls, and hiding them
  // would empty the menu its allowed trigger opens.
  applyToolbarAllowList() {
    const editor = this.editorElement
    if (!editor) return

    const toolbar = editor.querySelector("lexxy-toolbar")
    if (!toolbar) return

    const allowed = this.hasToolbarValue ? this.toolbarValue : []
    // An empty allow-list means the component asked for no toolbar at all, and
    // the gem was told `toolbar="false"` — so there is nothing to hide, and
    // hiding everything in a toolbar somebody else put there would be wrong.
    if (allowed.length === 0) return

    toolbar.querySelectorAll(":scope > button[name]").forEach((button) => {
      button.hidden = !allowed.includes(button.getAttribute("name"))
    })

    toolbar.querySelectorAll(`:scope > :is(${DROPDOWN_TAGS})`).forEach((dropdown) => {
      const trigger = dropdown.querySelector("[data-dropdown-trigger][name]")
      // No named trigger means the gem's own overflow menu, which it fills and
      // shows by itself as the toolbar narrows. Never ours to touch.
      if (!trigger) return
      dropdown.hidden = !allowed.includes(trigger.getAttribute("name"))
    })
  }

  // Turbo can insert a <lexxy-editor> that never built itself: the fragment was
  // parsed before the custom element definition loaded, so connectedCallback
  // ran against an undefined element and no editing surface exists. Taking the
  // element out and putting it back re-runs the upgrade. Only a STALLED editor
  // is touched — re-inserting a healthy one would throw away whatever the user
  // has typed.
  reinitializeStalledEditor() {
    const editor = this.editorElement
    if (!editor) return
    if (editor.querySelector("[contenteditable], [role='textbox']")) return

    const parent = editor.parentNode
    if (!parent) return
    const next = editor.nextSibling
    parent.removeChild(editor)
    parent.insertBefore(editor, next)
  }

  emit(name, detail = {}) {
    this.dispatch(name, { detail, prefix: "ui--text-editor-support" })
  }

  stripTags(html) {
    const div = document.createElement("div")
    div.innerHTML = html ?? ""
    return div.textContent || ""
  }

  // ------------------------------------------- basic engine (DEPRECATED)

  // Toolbar button click handler
  format(event) {
    event.preventDefault()

    if (this.disabledValue) return

    const button = event.currentTarget
    const command = button.dataset.command

    if (command === "link") {
      this.insertLink()
    } else {
      document.execCommand(command, false, null)
    }

    this.editorTarget.focus()
    this.syncHiddenInput()
    this.updateToolbarState()
  }

  // Insert or edit a link
  insertLink() {
    const selection = window.getSelection()
    const selectedText = selection.toString()
    const existingLink = this.getParentLink(selection)

    const url = prompt(
      "Enter URL:",
      existingLink ? existingLink.href : "https://",
    )

    if (url === null) return // Cancelled

    if (url === "") {
      // Remove link
      if (existingLink) {
        document.execCommand("unlink", false, null)
      }
    } else {
      if (selectedText || existingLink) {
        document.execCommand("createLink", false, url)
      } else {
        // No selection: insert the URL as linked text
        document.execCommand(
          "insertHTML",
          false,
          `<a href="${this.escapeHtml(url)}">${this.escapeHtml(url)}</a>`,
        )
      }
    }

    this.syncHiddenInput()
  }

  // Sync editor content to hidden input
  syncHiddenInput() {
    if (!this.hasHiddenTarget || !this.hasEditorTarget) return

    const content = this.editorTarget.innerHTML
    // Treat placeholder-only content as empty
    const isEmpty =
      !this.editorTarget.textContent.trim() &&
      !this.editorTarget.querySelector("img")
    this.hiddenTarget.value = isEmpty ? "" : content
  }

  // Show/hide placeholder based on content
  setupPlaceholder() {
    const editor = this.editorTarget
    const placeholder = editor.dataset.placeholder

    if (!placeholder) return

    const updatePlaceholder = () => {
      const isEmpty = !editor.textContent.trim() && !editor.querySelector("img")
      editor.classList.toggle("is-empty", isEmpty)
    }

    // Add CSS for placeholder via data attribute
    editor.addEventListener("input", () => {
      updatePlaceholder()
      this.syncHiddenInput()
      this.updateToolbarState()
      // Same event shape as the Lexxy engine emits, so a consumer written
      // against the public API works on either engine.
      this.emit("change", { html: this.getHTML(), text: this.getText() })
    })

    editor.addEventListener("focus", () => {
      this.element
        .querySelector("[class*='border']")
        ?.classList.add("ring-2", "ring-primary-500/20")
      this.emit("focus")
    })

    editor.addEventListener("blur", () => {
      updatePlaceholder()
      this.syncHiddenInput()
      this.element
        .querySelector("[class*='border']")
        ?.classList.remove("ring-2", "ring-primary-500/20")
      this.emit("blur")
    })

    // Initial state
    updatePlaceholder()
  }

  // Handle keyboard shortcuts (Ctrl/Cmd + B, I, U, K)
  setupKeyboardShortcuts() {
    this.editorTarget.addEventListener("keydown", (event) => {
      if (this.disabledValue) return

      const isModifier = event.ctrlKey || event.metaKey
      if (!isModifier) return

      const shortcuts = {
        b: "bold",
        i: "italic",
        u: "underline",
        k: "link",
      }

      const command = shortcuts[event.key.toLowerCase()]
      if (!command) return

      event.preventDefault()

      if (command === "link") {
        this.insertLink()
      } else {
        document.execCommand(command, false, null)
      }

      this.syncHiddenInput()
      this.updateToolbarState()
    })
  }

  // Update toolbar button active states based on current selection
  updateToolbarState() {
    if (!this.hasToolbarButtonTargets) return

    this.toolbarButtonTargets.forEach((button) => {
      const command = button.dataset.command

      if (command === "link") {
        const selection = window.getSelection()
        const hasLink = this.getParentLink(selection) !== null
        button.classList.toggle("bg-card-secondary", hasLink)
        button.classList.toggle("text-foreground", hasLink)
        button.setAttribute("aria-pressed", hasLink)
      } else {
        const isActive = document.queryCommandState(command)
        button.classList.toggle("bg-card-secondary", isActive)
        button.classList.toggle("text-foreground", isActive)
        button.setAttribute("aria-pressed", isActive)
      }
    })
  }

  // Find parent <a> element from current selection
  getParentLink(selection) {
    if (!selection || !selection.rangeCount) return null

    let node = selection.anchorNode
    while (node && node !== this.editorTarget) {
      if (node.nodeName === "A") return node
      node = node.parentNode
    }
    return null
  }

  // Escape HTML for safe insertion
  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
